import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { JiraIssue } from '../lib/api.js';

function statusColor(statusCategory: string): string {
  if (statusCategory === 'In Progress') return 'var(--accent-yellow)';
  if (statusCategory === 'Done') return 'var(--accent-green)';
  return 'var(--accent-blue)';
}

function priorityColor(priority: string | null): string {
  if (priority === 'Highest' || priority === 'High') return 'var(--accent-red)';
  if (priority === 'Medium') return 'var(--accent-orange)';
  return 'var(--accent-green)';
}

function IssueTypeIcon() {
  return (
    <span
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs"
      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
    >
      ●
    </span>
  );
}

function SkeletonRow() {
  return (
    <div className="rounded-xl p-3 animate-pulse" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-3">
        <div className="h-5 w-5 rounded-full" style={{ background: 'var(--border)' }} />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-24 rounded" style={{ background: 'var(--border)' }} />
          <div className="h-3 w-48 rounded" style={{ background: 'var(--border)' }} />
        </div>
        <div className="h-5 w-16 rounded-full" style={{ background: 'var(--border)' }} />
      </div>
    </div>
  );
}

export function JiraPanel({ ehrName }: { ehrName?: string }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['jira', debouncedSearch, ehrName],
    queryFn: () => api.listJiraIssues(debouncedSearch || undefined, ehrName),
    retry: false,
  });

  const issues: JiraIssue[] = data?.issues ?? [];

  const isNotConfigured =
    error instanceof Error && error.message.toLowerCase().includes('not configured');

  return (
    <div className="space-y-3">
      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Jira issues…"
        className="w-full rounded-xl px-3 py-2 text-sm outline-none"
        style={{
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border)',
          color: 'var(--text-primary)',
        }}
      />

      {/* Not configured notice */}
      {isNotConfigured && (
        <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>
          Configure Jira in Settings to enable ticket cards.
        </div>
      )}

      {/* Other error */}
      {error && !isNotConfigured && (
        <div className="text-sm" style={{ color: 'var(--accent-red)' }}>
          {error instanceof Error ? error.message : 'Failed to load issues'}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {/* Issues */}
      {!isLoading && !error && issues.length === 0 && (
        <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>No issues found.</div>
      )}

      {!isLoading && !error && issues.map((issue) => (
        <button
          key={issue.key}
          type="button"
          onClick={() => window.open(issue.url, '_blank')}
          className="w-full rounded-xl p-3 text-left transition-colors duration-100"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent-blue)')}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
        >
          <div className="flex items-start gap-3">
            <IssueTypeIcon />
            <div className="flex-1 min-w-0 space-y-1.5">
              {/* Key + summary row */}
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs shrink-0" style={{ color: 'var(--text-dimmed)' }}>
                  {issue.key}
                </span>
                <span className="truncate text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {issue.summary}
                </span>
              </div>
              {/* Detail pills */}
              <div className="flex flex-wrap gap-1.5">
                {issue.clientEmr && (
                  <span className="rounded px-1.5 py-0.5 text-xs" style={{ background: 'var(--accent-blue)22', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)33' }}>
                    {issue.clientEmr}
                  </span>
                )}
                {issue.goLiveDate && (
                  <span className="rounded px-1.5 py-0.5 text-xs" style={{ color: 'var(--text-dimmed)' }}>
                    🗓 {issue.goLiveDate}
                  </span>
                )}
                {issue.customerUrl && (
                  <a
                    href={issue.customerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="rounded px-1.5 py-0.5 text-xs truncate max-w-[160px] hover:underline"
                    style={{ color: 'var(--text-dimmed)' }}
                  >
                    {issue.customerUrl.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
            {/* Right: status pill, priority dot, assignee */}
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span
                className="rounded-full px-2 py-0.5 text-xs font-medium"
                style={{
                  background: `${statusColor(issue.statusCategory)}22`,
                  color: statusColor(issue.statusCategory),
                  border: `1px solid ${statusColor(issue.statusCategory)}44`,
                }}
              >
                {issue.status}
              </span>
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  title={issue.priority ?? 'None'}
                  style={{ background: priorityColor(issue.priority) }}
                />
                {issue.assignee && (
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    title={issue.assignee}
                    style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                  >
                    {issue.assignee[0]?.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
